import { DiplomaIdType, SESSION_STORAGE_UUID_KEY } from '@/data/constants'
import {
  Course,
  ExtraPoints,
  nullExtraPoints,
  nullUserDetails,
  UserDetails,
} from '@/data/models'
import { create } from 'zustand'
import coursesList from '../data/coursesList'
import short from 'short-uuid'

export interface CalculatorStore {
  status?: 'new' | 'modified' | 'saved'
  userDetails: UserDetails
  extraPoints: ExtraPoints
  courses: Course[]
  inputDiploma: DiplomaIdType
  improvedDiploma: DiplomaIdType
  dreamStudy: string
  uuid: string
  show235: boolean
  setData: (newData: Partial<CalculatorStore>) => void
  setStatus: (status: 'new' | 'modified' | 'saved') => void
  setUuid: () => void
  setBirthYear: (birthYear: number) => void
  setExtraPoints: (extraPoints: ExtraPoints) => void
  setStudyStartYear: (studyStartYear: number) => void
  setEmail: (email: string) => void
  setDreamStudy: (dreamStudy: string) => void
  setDiploma: (diploma: DiplomaIdType, type: 'improved' | 'input') => void
  addNewCourse: (course: Course) => void
  addCourse: (courseId: string) => void
  removeCourse: (courseId: string) => void
  changeGrade: (
    courseId: string,
    grade: number,
    type: 'grade' | 'exam' | 'exam2' | 'improved' | 'improved2'
  ) => void
  increaseNumExams: (courseId: string) => void
  decreaseNumExams: (courseId: string) => void
  setShow235: (show235: boolean) => void
  toggleCourseVisibility: (courseId: string) => void
}

export const useCalculatorStore = create<CalculatorStore>((set) => ({
  status: 'new',
  userDetails: nullUserDetails,
  extraPoints: nullExtraPoints,
  courses: coursesList,
  inputDiploma: 'SSREAL',
  improvedDiploma: 'SSREAL',
  dreamStudy: '',
  uuid: '',
  show235: false,

  setData: (newData: Partial<CalculatorStore>) => {
    set(() => ({ status: 'modified' }))
    set((state) => ({ ...state, ...newData }))
  },

  setStatus: (status: 'new' | 'modified' | 'saved') => {
    set(() => ({ status }))
  },

  setUuid: () => {
    set((state) => {
      if (state.uuid !== '') {
        return state
      }

      try {
        const storedUuid = sessionStorage.getItem(SESSION_STORAGE_UUID_KEY)
        if (storedUuid) {
          return { uuid: storedUuid }
        }
      } catch (error) {
        console.warn('Failed to read from sessionStorage:', error)
      }

      const newUuid = short.generate()
      try {
        sessionStorage.setItem(SESSION_STORAGE_UUID_KEY, newUuid)
      } catch (error) {
        console.warn('Failed to write to sessionStorage:', error)
      }
      return { uuid: newUuid }
    })
  },

  setBirthYear: (birthyear: number) => {
    set(() => ({ status: 'modified' }))
    set((state) => ({
      ...state,
      userDetails: { ...state.userDetails, birthyear },
    }))
  },

  setExtraPoints: (extraPoints: ExtraPoints) => {
    set(() => ({ status: 'modified' }))
    set((state) => ({ ...state, extraPoints }))
  },

  setStudyStartYear: (studyStartYear: number) => {
    set(() => ({ status: 'modified' }))
    set((state) => ({
      ...state,
      userDetails: { ...state.userDetails, studyStartYear },
    }))
  },

  setEmail: (email: string) => {
    set(() => ({ status: 'modified' }))
    set((state) => ({ ...state, userDetails: { ...state.userDetails, email } }))
  },

  setDreamStudy: (dreamStudy: string) => {
    set(() => ({ status: 'modified' }))
    set((state) => ({ ...state, dreamStudy }))
  },

  setDiploma: (diploma: DiplomaIdType, type: 'improved' | 'input') => {
    set(() => ({ status: 'modified' }))
    set((state) => ({
      ...state,
      ...(type === 'input'
        ? { improvedDiploma: diploma, inputDiploma: diploma }
        : { improvedDiploma: diploma }),
    }))
  },

  addNewCourse: (course: Course) => {
    set(() => ({ status: 'modified' }))
    set((state) => ({ ...state, courses: [...state.courses, course] }))
  },

  addCourse: (courseId: string) => {
    set(() => ({ status: 'modified' }))
    set((state) => ({
      ...state,
      courses: state.courses.map((c) =>
        c.id === courseId
          ? {
              ...c,
              included: true,
            }
          : c
      ),
    }))
  },

  removeCourse: (courseId: string) => {
    set(() => ({ status: 'modified' }))
    set((state) => ({
      courses: state.courses.map((c) =>
        c.id === courseId
          ? {
              ...c,
              included: false,
            }
          : c
      ),
    }))
  },

  changeGrade: (
    courseId: string,
    grade: number,
    type: 'grade' | 'exam' | 'exam2' | 'improved' | 'improved2'
  ) => {
    set(() => ({ status: 'modified' }))
    set((state) => ({
      courses: state.courses.map((c) =>
        c.id === courseId ? { ...c, [type]: grade } : c
      ),
    }))
  },

  increaseNumExams: (courseId: string) => {
    set(() => ({ status: 'modified' }))
    set((state) => ({
      courses: state.courses.map((c) =>
        c.id === courseId
          ? { ...c, numExams: (c.numExams + 1) as 0 | 1 | 2 }
          : c
      ),
    }))
  },

  decreaseNumExams: (courseId: string) => {
    set(() => ({ status: 'modified' }))
    set((state) => ({
      courses: state.courses.map((c) =>
        c.id === courseId
          ? { ...c, numExams: (c.numExams - 1) as 0 | 1 | 2 }
          : c
      ),
    }))
  },

  setShow235: (show235: boolean) => {
    set(() => ({ status: 'modified' }))
    set((state) => ({ ...state, show235 }))
  },

  toggleCourseVisibility: (courseId: string) => {
    set(() => ({ status: 'modified' }))
    set((state) => ({
      courses: state.courses.map((c) =>
        c.id === courseId ? { ...c, visible: c.visible === false } : c
      ),
    }))
  },
}))
